import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function HeroSection() {
  const { t } = useTranslation();

  return (
    <section className="relative w-full min-h-screen flex items-end pb-0 overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20artistic%20depiction%20of%20interconnected%20electrical%20grid%20and%20circuit%20patterns%20with%20warm%20copper%20and%20amber%20tones%20blending%20into%20dark%20steel%20textures%2C%20minimalist%20geometric%20lines%2C%20soft%20gradient%20lighting%2C%20modern%20industrial%20aesthetic%2C%20clean%20atmospheric%20corporate%20style%2C%20no%20text%2C%20abstract%20art&width=1920&height=1080&seq=hero-main-2026-v2&orientation=landscape"
          alt="Erdemir Elektrik endüstriyel çözümler"
          className="w-full h-full object-cover object-center"
          title="Erdemir Elektrik Pano Otomasyon İnşaat"
        />
      </div>

      {/* Complex overlay for depth */}
      <div className="absolute inset-0 bg-gradient-to-br from-foreground-950/85 via-foreground-950/55 to-foreground-950/75"></div>
      <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-transparent to-transparent"></div>

      {/* Subtle geometric overlay pattern */}
      <div className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.4) 1px, transparent 0)',
          backgroundSize: '40px 40px',
        }}
      ></div>

      {/* Main content */}
      <div className="container-page relative z-10 w-full pb-20 md:pb-28 pt-32 md:pt-44">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          {/* Left - Main headline area */}
          <div className="lg:col-span-7">
            {/* Established badge */}
            <div className="inline-flex items-center gap-3 mb-10 px-4 py-2 border border-background-50/20 rounded-full bg-background-50/5 backdrop-blur-sm">
              <div className="w-2 h-2 rounded-full bg-accent-400 animate-pulse"></div>
              <span className="text-background-50/80 text-xs font-medium tracking-widest uppercase">
                1983&apos;ten Beri
              </span>
              <span className="w-px h-4 bg-background-50/20"></span>
              <span className="text-background-50/60 text-xs font-medium tracking-wider uppercase">
                İstanbul / Türkiye
              </span>
            </div>

            {/* Main heading */}
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-black leading-[1.05] tracking-tight mb-6">
              <span className="text-background-50 block">
                Elektrikte
              </span>
              <span className="text-background-50/90 font-light block">
                Güvenilir Çözüm
              </span>
              <span className="text-primary-400 font-bold block">
                Ortağınız
              </span>
            </h1>

            {/* Subtitle */}
            <p className="text-background-50/60 text-base md:text-lg max-w-lg leading-relaxed mb-10 font-light">
              1983&apos;ten bu yana kalite, süreklilik ve müşteri memnuniyeti ile
              elektrik pano imalatı, otomasyon ve taahhüt alanlarında
              Türkiye&apos;nin önde gelen kurumsal çözüm sağlayıcısı.
            </p>

            {/* CTA buttons */}
            <div className="flex flex-col sm:flex-row items-start gap-4">
              <Link
                to="/uygulamalarimiz"
                className="group inline-flex items-center gap-3 px-8 py-4 bg-primary-500 text-background-50 font-semibold rounded-xl hover:bg-primary-400 transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg shadow-primary-500/25 hover:shadow-xl hover:shadow-primary-500/30 hover:-translate-y-0.5"
              >
                <span className="text-sm">Projelerimizi İnceleyin</span>
                <i className="ri-arrow-right-line text-lg w-5 h-5 flex items-center justify-center group-hover:translate-x-1 transition-transform"></i>
              </Link>
              <Link
                to="/iletisim"
                className="group inline-flex items-center gap-3 px-8 py-4 border border-background-50/25 text-background-50 font-semibold rounded-xl hover:bg-background-50/10 hover:border-background-50/40 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <span className="text-sm">Bize Ulaşın</span>
                <i className="ri-mail-send-line text-lg w-5 h-5 flex items-center justify-center"></i>
              </Link>
            </div>
          </div>

          {/* Right - Stats and visual card */}
          <div className="lg:col-span-5">
            <div className="grid grid-cols-2 gap-4 mb-6">
              {/* Stat 1 */}
              <div className="bg-background-50/5 backdrop-blur-sm border border-background-50/10 rounded-2xl p-5 md:p-6 hover:bg-background-50/8 transition-all duration-300">
                <div className="text-3xl md:text-4xl font-black text-background-50 mb-1 tracking-tight">
                  40+
                </div>
                <div className="text-background-50/50 text-xs md:text-sm font-medium leading-snug">
                  Yıllık Sektör<br />Deneyimi
                </div>
              </div>

              {/* Stat 2 */}
              <div className="bg-background-50/5 backdrop-blur-sm border border-background-50/10 rounded-2xl p-5 md:p-6 hover:bg-background-50/8 transition-all duration-300">
                <div className="text-3xl md:text-4xl font-black text-background-50 mb-1 tracking-tight">
                  2000+
                </div>
                <div className="text-background-50/50 text-xs md:text-sm font-medium leading-snug">
                  Tamamlanan<br />Proje
                </div>
              </div>

              {/* Stat 3 */}
              <div className="bg-background-50/5 backdrop-blur-sm border border-background-50/10 rounded-2xl p-5 md:p-6 hover:bg-background-50/8 transition-all duration-300">
                <div className="text-3xl md:text-4xl font-black text-accent-400 mb-1 tracking-tight">
                  25+
                </div>
                <div className="text-background-50/50 text-xs md:text-sm font-medium leading-snug">
                  Çözüm Ortağı<br />Marka
                </div>
              </div>

              {/* Stat 4 */}
              <div className="bg-background-50/5 backdrop-blur-sm border border-background-50/10 rounded-2xl p-5 md:p-6 hover:bg-background-50/8 transition-all duration-300">
                <div className="text-3xl md:text-4xl font-black text-background-50 mb-1 tracking-tight">
                  500+
                </div>
                <div className="text-background-50/50 text-xs md:text-sm font-medium leading-snug">
                  Kurumsal<br />Müşteri
                </div>
              </div>
            </div>

            {/* Trust highlights */}
            <div className="bg-background-50/5 backdrop-blur-sm border border-background-50/10 rounded-2xl p-5 md:p-6">
              <p className="text-background-50/70 text-xs md:text-sm leading-relaxed font-light mb-4">
                &ldquo;Kurulduğu günden bu yana kalite ve müşteri memnuniyetini temel ilke olarak benimseyen firmamız,
                sektörde edindiği deneyim ve güçlü iş ağıyla müşterilerine değer katmaya devam etmektedir.&rdquo;
              </p>
              <div className="flex items-center gap-3 pt-3 border-t border-background-50/10">
                <div className="w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center">
                  <span className="text-primary-300 text-sm font-bold">NE</span>
                </div>
                <div>
                  <div className="text-background-50/80 text-xs font-semibold">Nazım Erdemir</div>
                  <div className="text-background-50/40 text-[11px]">Kurucu</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 animate-bounce">
        <span className="text-background-50/30 text-[10px] uppercase tracking-[0.2em] font-medium">Aşağı Kaydır</span>
        <i className="ri-arrow-down-line text-background-50/30 w-4 h-4 flex items-center justify-center"></i>
      </div>

      {/* Bottom gradient transition */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background-50 to-transparent pointer-events-none"></div>
    </section>
  );
}